import * as z from 'zod';

import {
  cateringOrderSchema,
  deliveryTipSchema,
  orderDeliveryAddressSchema,
  orderLineItemComboItemSchema,
  orderLineItemModifierSchema,
  orderLineItemSchema,
  orderSchema,
} from '../order/schemas';

export const cfaLoyaltySchema = z.enum(['Chick-fil-a One', 'Silver', 'Red', 'Signature']);

/** Mirrors rex-catering `MemberAccountType`. */
export const customerAccountTypeSchema = z.enum({
  UNSPECIFIED: 0,
  MEMBER: 1,
  GUEST: 2,
  FEEDBACK: 3,
  DEACTIVATED: 4,
  CANADA_MEMBER: 5,
  CANADA_GUEST: 6,
});

export const dashboardOrderModifierSchema = z.object({
  retailPrice: z.number().optional(),
  priceAdjustment: z.number().optional(),
  calculatedPrice: z.number().optional(),
  quantity: z.number().optional(),
  itemTag: z.string(),
});

export const dashboardOrderComboItemSchema = z.object({
  quantity: z.number().optional(),
  priceAdjustment: z.number().optional(),
  retailPrice: z.number().optional(),
  itemTag: z.string(),
  name: z.string().optional(),
  modifiers: z.array(dashboardOrderModifierSchema).optional(),
});

export const dashboardOrderLineItemSchema = z.object({
  quantity: z.number(),
  itemTag: z.string(),
  retailPrice: z.number().optional(),
  compValue: z.number().optional(),
  promoFree: z.boolean().optional(),
  name: z.string().optional(),
  comboItems: z.array(dashboardOrderComboItemSchema).optional(),
  modifiers: z.array(dashboardOrderModifierSchema).optional(),
  get subItems() {
    return z.array(dashboardOrderLineItemSchema).optional();
  },
  specialInstructions: z.string().optional(),
});

export const dashboardOrderSchema = cateringOrderSchema
  .omit({
    deliveryAddress: true,
  })
  .extend({
    transId: z.string().optional(),
    cfaId: z.string(),
    cfaLoyalty: cfaLoyaltySchema.optional(),
    subTotalAmount: z.number(),
    taxAmount: z.number(),
    promiseDateTime: z.string(),
    time: z.string().optional(),
    utcTime: z.string().optional(),
    guestCount: z.number().optional(),
    clientId: z.string().optional(),
    status: z.string(),
    phone: z.string().optional(),
    email: z.string().optional(),
    displayStatus: z
      .object({
        statusName: z.string(),
        statusIcon: z.string(),
      })
      .optional(),
    timestamps: z
      .object({
        cancel: z.string().optional(),
        paymentPending: z.string().optional(),
        submit: z.string().optional(),
        create: z.string().optional(),
      })
      .optional(),
    givenName: z.string().optional(),
    familyName: z.string().optional(),
    accountType: customerAccountTypeSchema.optional(),
    destination: z.string(),
    totalAmount: z.number().optional(),
    lineItems: z.array(dashboardOrderLineItemSchema).optional(),
    deliveryTip: deliveryTipSchema.optional(),
    fullError: z
      .object({
        createdAt: z.string(),
      })
      .optional(),
    deliveryAddress: z.union([orderDeliveryAddressSchema, z.string()]).optional(),
    sendCustomerNotifications: z.boolean().optional(),
  });

export const cfaErrorSchema = z.looseObject({
  userMessage: z.string().optional(),
});

/** Response from `POST /users/1.0/me/orders/:orderId/receiptemail` when a JSON body is returned. */
export const resendReceiptEmailResponseSchema = z.object({
  cfaError: cfaErrorSchema.optional(),
});

/** Customer from rex-catering `GetCustomerByID`, optionally scoped to an order. */
export const customerSchema = z.object({
  givenName: z.string(),
  familyName: z.string(),
  emailAddress: z.string(),
  phoneNumber: z.string(),
  accountType: customerAccountTypeSchema,
  id: z.string().optional(),
});

export type CfaLoyalty = z.infer<typeof cfaLoyaltySchema>;
export type CfaError = z.infer<typeof cfaErrorSchema>;
export type CustomerAccountType = z.infer<typeof customerAccountTypeSchema>;
export type ResendReceiptEmailResponse = z.infer<typeof resendReceiptEmailResponseSchema>;
export type Customer = z.infer<typeof customerSchema>;
export type DashboardOrderModifier = z.infer<typeof dashboardOrderModifierSchema>;
export type DashboardOrderComboItem = z.infer<typeof dashboardOrderComboItemSchema>;
export type DashboardOrderLineItem = z.infer<typeof dashboardOrderLineItemSchema>;
export type DashboardOrder = z.infer<typeof dashboardOrderSchema>;

const orderReportCoordinatesSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

// The Order API returns modifierId as a JSON number, but the shared modifier schema types it as a string
// because it also builds the InitiateOrder request. Nothing reads it from a report, so drop it here.
const orderReportModifierSchema = orderLineItemModifierSchema.omit({ modifierId: true });

const orderReportLineItemSchema = orderLineItemSchema.extend({
  modifiers: z.array(orderReportModifierSchema).optional(),
  comboItems: z
    .array(orderLineItemComboItemSchema.extend({ modifiers: z.array(orderReportModifierSchema).optional() }))
    .optional(),
});

// The Order API serializes nested objects that have no @JsonInclude as explicit nulls, e.g. rating.tags
// and user.phoneNumber. The shared schemas only allow a field to be missing, so drop null properties
// before parsing. That keeps the parsed types identical to the shared ones.
const stripNullProperties = (value: unknown) =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).filter(([, property]) => property !== null))
    : value;

const nullPropertiesAsMissing = <T extends z.ZodType>(schema: T) => z.preprocess(stripNullProperties, schema);

// The Order API sends isWaived: null on fees (e.g. Bag Fee), which the shared fee schema rejects.
// Nothing reads fees from a report, so drop both arrays here.
export const orderReportSchema = cateringOrderSchema
  .omit({ additionalFees: true, deliveryFees: true })
  .extend({
    familyName: z.string().optional(),
    rating: nullPropertiesAsMissing(orderSchema.shape.rating.unwrap()).optional(),
    user: nullPropertiesAsMissing(orderSchema.shape.user.unwrap()).optional(),
    secondaryContact: nullPropertiesAsMissing(orderSchema.shape.secondaryContact.unwrap()).optional(),
    externalOrderInfo: nullPropertiesAsMissing(orderSchema.shape.externalOrderInfo.unwrap()).optional(),
    thirdPartyInAppDetails: nullPropertiesAsMissing(
      orderSchema.shape.thirdPartyInAppDetails.unwrap(),
    ).optional(),
    smallDeliveryOrderTiers: z
      .array(nullPropertiesAsMissing(orderSchema.shape.smallDeliveryOrderTiers.unwrap().element))
      .optional(),
    lineItems: z.array(orderReportLineItemSchema).optional(),
    deliveryAddress: z
      .record(z.string(), z.union([z.string(), orderReportCoordinatesSchema]).nullable())
      .optional(),
    transactionId: z.string().nullable().optional(),
    deliveryTip: z
      .object({
        tipAmount: z.number().nullable().optional(),
        tipPercentOfSale: z.number().nullable().optional(),
      })
      .optional(),
  });

export type OrderReport = z.infer<typeof orderReportSchema>;
